import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/configureStore";
import autobahn from "autobahn";
import { SocketMessage } from "./types";
import { VenueSessionStatus } from "../../app/types";
import {
  useGetSessionsQuery,
  useJoinSessionQuery,
  useSession,
  useTenant,
  useUser,
  useVenue,
} from "../api/helpers";
import { authSelector } from "../auth/selectors";
import { receiveChatMessage } from "../chat/chatSlice";

const useSockets = () => {
  const venueSession = useSession();
  const venue = useVenue();

  const keepAliveTimer = useRef<NodeJS.Timeout>();
  const connection = useRef<autobahn.Connection>();
  const tenant = useTenant();
  const user = useUser();
  const dispatch = useAppDispatch();
  const { access_token } = useAppSelector(authSelector);

  const { data, refetch: refetchJoin } = useJoinSessionQuery();
  const { refetch: refetchSessions } = useGetSessionsQuery();

  const topics = useRef<string[]>([]);
  const hasSessionJoinToken = useRef(false);
  const currentSessionId = useRef<number>();
  const sessionStatus = useRef<VenueSessionStatus>();

  useEffect(() => {
    currentSessionId.current = venueSession?.sessionId;
  }, [venueSession?.sessionId]);

  useEffect(() => {
    sessionStatus.current = venueSession?.status;
  }, [venueSession?.status]);

  useEffect(() => {
    if (data !== undefined) {
      hasSessionJoinToken.current = true;
    }
  }, [data]);

  const chatTopic = useMemo(
    () => venueSession?.chatTopic ?? venue?.chatTopic,
    [venueSession?.chatTopic, venue?.chatTopic],
  );

  const onChatMsg: autobahn.SubscribeHandler = useCallback(
    (args) => {
      const message = JSON.parse(args[0]) as SocketMessage;
      switch (message.event) {
        case "chat_msg":
          dispatch(receiveChatMessage(message));
          break;
        case "venue_status_changed":
          if (!hasSessionJoinToken.current) {
            refetchJoin();
          }
          refetchSessions();
          break;
        default:
          break;
      }
    },
    [dispatch, refetchJoin, refetchSessions],
  );

  useEffect(() => {
    if (chatTopic && !topics.current.includes(chatTopic)) {
      topics.current.push(chatTopic);

      connection.current?.session?.subscribe(chatTopic, onChatMsg);
    }
  }, [chatTopic, onChatMsg]);

  useEffect(() => {
    if (tenant?.tenantId && user?.profileId && access_token) {
      const authid = `${tenant.tenantId}_${user.profileId}`;

      connection.current = new autobahn.Connection({
        url: tenant.websocketDomain,
        realm: "realm",
        authid,
        authmethods: ["wampcra"],
        onchallenge: (session, method, extra) =>
          autobahn.auth_cra.sign(access_token, extra.challenge),
        max_retries: -1,
      });

      connection.current.onopen = (session) => {
        keepAliveTimer.current = setInterval(() => {
          if (session.isOpen) {
            if (
              currentSessionId.current &&
              hasSessionJoinToken &&
              sessionStatus.current === "InProgress"
            ) {
              session.call("com.workoutloud.venueAnalytics", null, {
                session: currentSessionId.current,
              });
            } else {
              session.call("com.workoutloud.keepAlive", []);
            }
          }
        }, 45000);

        topics.current.forEach((topic) => {
          session.subscribe(topic, onChatMsg);
        });
      };

      connection.current.onclose = () => {
        if (keepAliveTimer.current) {
          clearInterval(keepAliveTimer.current);
        }
        return false;
      };

      connection.current.open();
    }
  }, [
    access_token,
    onChatMsg,
    tenant?.tenantId,
    tenant?.websocketDomain,
    user?.profileId,
  ]);
};

export default useSockets;
